import { FC } from 'react';
import { createUseStyles } from 'react-jss';
import { IInsightsFilter } from './agencyDigest';
import { BRAND, CHART_INK, describeFilter } from './agencyChartTheme';

const useStyles = createUseStyles({
    // The narrowing bar takes the coral so it reads as a temporary state laid over the tab rather
    // than another panel of it, and so it is impossible to leave one on without noticing.
    bar: {
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        flexWrap: 'wrap' as const,
        padding: '10px 14px',
        borderRadius: 10,
        border: `1px solid ${BRAND.coral}`,
        background: BRAND.coralWash
    },

    text: {
        flex: 1,
        minWidth: 180,
        fontSize: 13,
        lineHeight: 1.45,
        color: CHART_INK.strongLabel
    },

    strong: {
        fontWeight: 600,
        color: BRAND.deepTeal
    },

    action: {
        border: `1px solid ${BRAND.tealEdge}`,
        borderRadius: 20,
        padding: '5px 13px',
        font: 'inherit',
        fontSize: 12.5,
        color: BRAND.deepTeal,
        background: 'var(--ion-background-color)',
        cursor: 'pointer'
    }
});

/**
 * What the tab is currently narrowed to, in words, with the way back out.
 *
 * The counts are here rather than only on the lists because a narrowing set from one lens has to
 * mean something on the other: a reader who picked a week on the chart can see how many clients it
 * came to without changing lens first.
 */
export const InsightsFilterBar: FC<{
    filter: IInsightsFilter;
    clientCount: number;
    findingCount: number;
    /** Offered only where the matching clients are not already on screen. */
    onSeeWho?: () => void;
    onClear: () => void;
}> = ({ filter, clientCount, findingCount, onSeeWho, onClear }) => {
    const classes = useStyles();

    return (
        <div className={classes.bar} data-id="insights-filter-bar">
            <span className={classes.text}>
                Showing <span className={classes.strong}>{describeFilter(filter)}</span>:
                {' '}{clientCount} client{clientCount === 1 ? '' : 's'},
                {' '}{findingCount} finding{findingCount === 1 ? '' : 's'}.
            </span>
            {onSeeWho && (
                <button
                    type="button"
                    className={classes.action}
                    data-id="insights-filter-see-who"
                    onClick={onSeeWho}
                >
                    See who
                </button>
            )}
            <button
                type="button"
                className={classes.action}
                data-id="insights-filter-clear"
                onClick={onClear}
            >
                Clear
            </button>
        </div>
    );
};
